import Vue from 'vue'
import { ToastPlugin, ModalPlugin, BootstrapVue } from 'bootstrap-vue'
import VueCompositionAPI from '@vue/composition-api'

// Vuejs Logger for better logging
import VueLogger from 'vuejs-logger'

import { axiosIns } from '@/libs/axios'
import vSelect from 'vue-select'
import defaultRouter from './router'
import maintenanceRouter from './router/maintenance'
import store from './store'
import App from './App.vue'

// Global Components
import './global-components'

// 3rd party plugins
import '@/libs/portal-vue'
import '@/libs/toastification'

// Importing validation
import '@/@core/utils/validations/validations'

import 'vue-select/dist/vue-select.css'

// Sentry imports
import * as Sentry from '@sentry/vue'

// importing sanityplugin
import sanityPlugin from './plugins/sanity'

// importing ellipse shaped progress bar
// import VueEllipseProgress from 'vue-ellipse-progress'

const isMaintenance = process.env.VUE_APP_MAINTENANCE === 'true' || false
const router = isMaintenance ? maintenanceRouter : defaultRouter

const isProduction = process.env.NODE_ENV === 'production'
const options = {
  isEnabled: true,
  logLevel: isProduction ? 'error' : 'debug',
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: '|',
  showConsoleColors: true,
}

Vue.use(VueLogger, options)

// BSV Plugin Registration
Vue.use(ToastPlugin)
Vue.use(ModalPlugin)
Vue.use(BootstrapVue)

// Composition API
Vue.use(VueCompositionAPI)

// sanity plugin
Vue.use(sanityPlugin)

// ellipse progress bar
// Vue.use(VueEllipseProgress)
// Custom directives

// Custom directive to detect click outside an element
Vue.directive('click-outside', {
  inserted: (el, binding, vnode) => {
    // assign event to the element
    // eslint-disable-next-line no-param-reassign
    el.clickOutsideEvent = event => {
      // here we check if the click event is outside the element and it's children
      if (!(el === event.target || el.contains(event.target))) {
        // if clicked outside, call the provided method
        vnode.context[binding.expression](event)
      }
    }
    // register click and touch events
    document.body.addEventListener('click', el.clickOutsideEvent)
    document.body.addEventListener('touchstart', el.clickOutsideEvent)
  },
  unbind(el) {
    // unregister click and touch events before the element is unmounted
    document.body.removeEventListener('click', el.clickOutsideEvent)
    document.body.removeEventListener('touchstart', el.clickOutsideEvent)
  },
  stopProp(event) {
    event.stopPropagation()
  },
  // bind(el, binding, vnode) {
  //   // eslint-disable-next-line no-param-reassign
  //   el.clickOutsideEvent = event => {
  //     if (!(el === event.target || el.contains(event.target))) {
  //       vnode.context[binding.expression](event)
  //     }
  //   }
  //   document.body.addEventListener('click', el.clickOutsideEvent)
  // },
  // unbind(el) {
  //   document.body.removeEventListener('click', el.clickOutsideEvent)
  // },
})
// Custom directive for loading spinner in buttons
Vue.directive('loading', {
  update(el, binding, vnode, oldVnode) {
    if (typeof binding.value === 'object') {
      /* eslint-disable no-param-reassign */
      /* eslint-disable no-nested-ternary */
      if (binding.value.loading === true) {
        el.innerHTML = `<div class="d-flex aling-items-center"><div class="spinner-border spinner-border-${
          binding.value.size ? binding.value.size : 'sm'
        } ${
          binding.value.variant ? `text-${binding.value.variant}` : 'text-white'
        }" style="${
          binding.value.text ? 'margin-right: 0.5rem;' : ''
        }" role="status"></div><span class="${
          binding.value.variant ? `text-${binding.value.variant}` : 'text-white'
        }">${
          binding.value.text
            ? binding.value.text === true
              ? 'Loading...'
              : binding.value.text
            : ''
        }</span>`
      } else {
        el.innerHTML = oldVnode.children[0].text
      }
    }
  },
})

Vue.component('v-select', vSelect)

// import core styles
require('@core/scss/core.scss')

// import assets styles
require('@/assets/scss/style.scss')

// Custom Mixins
Vue.mixin({
  data() {
    return {
      slideType: 'question',
    }
  },
  methods: {
    getSlideType() {
      return this.slideType
    },
    nextSlide() {
      setTimeout(() => {
        this.$emit('next-slide')
      }, 0)
    },
  },
})
Vue.mixin({
  methods: {
    // =============================================================================
    // Open global overLay/loading
    // =============================================================================
    showLoading(text, variant = 'primary') {
      this.$store.commit('app/TOGGLE_OVERLAY', { text, variant })
    },
    // =============================================================================
    // Close global overLay/loading
    // =============================================================================
    closeLoading() {
      this.$store.commit('app/TOGGLE_OVERLAY', null)
    },
    // =============================================================================
    // Global function to handle axios errors
    // =============================================================================
    handleError(error) {
      if (error.internal === true) return
      let title
      let text
      let color
      if (!error.response) {
        [title, text, color] = ['NETWORK ERROR', 'Try Reloading', 'danger']
      } else {
        const code = error.response.status
        const response = error.response.data
        if (code === 400) {
          [title, text, color] = ['Invalid Input', response.error || response, 'primary']
        }
        if (code === 401) {
          [title, text, color] = [
            'Permission Denied',
            'Please contact the system admin',
            'primary',
          ]
        }
        if (code === 500) {
          [title, text, color] = [
            'Internal Server Error',
            response.message,
            'danger',
          ]
        }
        if (code === 401) {
          [title, text, color] = [
            'Please login again',
            response.message,
            'primary',
          ]
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
        }
      }
      this.closeLoading()
      this.$bvToast.toast(text, {
        title,
        variant: color,
        solid: true,
      })
    },
  },
})

Vue.config.productionTip = false

Vue.config.optionMergeStrategies.myOption = (toVal, fromVal) => {
  if (fromVal === 'question' && toVal !== 'question') {
    return toVal
  }
  return fromVal
}

Sentry.init({
  Vue,
  dsn: 'https://e0943ba15e5241aa8a1b3c416685edaf@o1054880.ingest.sentry.io/6041066',
  debug: process.env.VUE_APP_SENTRY_ENV !== 'production', // Enable debug mode for non-production environments
  release: `my-project-name@${process.env.npm_package_version}`,
  environment: process.env.VUE_APP_SENTRY_ENV, // Use the new Sentry environment variable

  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration()],
  tracePropagationTargets: [process.env.VUE_APP_BASE_URL, /^\//],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})
new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app')

Vue.prototype.$http = axiosIns
